<template>
    <div data-testid="profile-experience-item-date">
        <span>{{ datesAsString }}</span>
        <span v-if="isVisibleRange">
            {{ rangeAsString }}
        </span>
    </div>
</template>

<script lang="ts" setup>
    import {computed} from 'vue';
    import {intervalToDuration} from 'date-fns';
    import {useTranslation, useFormat} from '#imports';

    const {t} = useTranslation();

    const props = defineProps<{
        startDate: Date;
        endDate?: Date;
        current: boolean;
    }>();

    const {formatDate} = useFormat();

    const datesAsString = computed(() => {
        const options: Intl.DateTimeFormatOptions = {year: 'numeric', month: 'long'};
        const startDateFormatted = formatDate(props.startDate, options);

        if (props.current) {
            return `${startDateFormatted} - ${t('profile.xp.reco.humanize.time.today')}`;
        }

        if (props.endDate) {
            const endDateFormatted = formatDate(props.endDate, options);
            return `${startDateFormatted} - ${endDateFormatted}`;
        }

        return startDateFormatted;
    });

    const experienceRange = computed(() => {
        const end = props.current ? new Date() : (props.endDate ?? new Date());
        const {years = 0, months = 0, days = 0} = intervalToDuration({start: props.startDate, end});

        let totalYears = years;
        let totalMonths = months + (days > 15 ? 1 : 0);

        if (totalMonths >= 12) {
            totalYears += 1;
            totalMonths = 0;
        }

        return {years: totalYears, months: totalMonths};
    });

    const isVisibleRange = computed(() => !!experienceRange.value.years || !!experienceRange.value.months);
    const rangeAsString = computed(() => {
        const years = experienceRange.value.years ? t('profile.experience.period.years', {nbOfYears: experienceRange.value.years}) : '';
        const months = experienceRange.value.months ? t('profile.experience.period.months', {nbOfMonths: experienceRange.value.months}) : '';
        const optAnd = !!years && !!months ? ` ${t('general.and')} ` : '';

        return ` (${years}${optAnd}${months})`;
    });
</script>
